// =====================================================================
// myCRM — Accounts (list + detail with 8 tabs)
// =====================================================================

function NewAccountDialog({ onClose, navigate }) {
  const [form, setForm] = useState({ name: "", segment: "Mid-market", industry: "", hq: "", type: "Prospect", icp: "Tier 2", website: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      const id = "a_" + Math.random().toString(36).slice(2, 9);
      const res = await tpAPI.accounts.create({ id, ...form, techStack: [], challenges: [] });
      onClose();
      navigate("sales/accounts/" + (res.id || id));
    } catch(e) { setError(e.message || "Failed to create account."); setSaving(false); }
  }
  return (
    <Dialog title="New account" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Creating…" : "Create account"}</button>
    </>}>
      <div className="form-field"><label>Account name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Acme Corp" autoFocus />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Segment</label>
          <select value={form.segment} onChange={e => set("segment", e.target.value)}>
            <option>SME</option><option>Mid-market</option><option>Enterprise</option>
          </select>
        </div>
        <div className="form-field"><label>Type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>Prospect</option><option>Customer</option><option>Partner</option><option>Investor</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Industry</label>
          <select value={form.industry} onChange={e => set("industry", e.target.value)}>
            <option value="">— None —</option>
            {D.INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
          </select>
        </div>
        <div className="form-field"><label>HQ</label>
          <input value={form.hq} onChange={e => set("hq", e.target.value)} placeholder="London, UK" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>ICP tier</label>
          <select value={form.icp} onChange={e => set("icp", e.target.value)}>
            <option>Tier 1</option><option>Tier 2</option><option>Tier 3</option><option>Disqualified</option>
          </select>
        </div>
        <div className="form-field"><label>Website</label>
          <input value={form.website} onChange={e => set("website", e.target.value)} placeholder="acme.com" />
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function EditAccountDialog({ account, onClose }) {
  const [form, setForm] = useState({
    name: account.name || "",
    website: account.website || "",
    industry: account.industry || "",
    subIndustry: account.subIndustry || "",
    segment: account.segment || "Mid-market",
    type: account.type || "Prospect",
    icp: account.icp || "Tier 2",
    headcount: account.headcount == null ? "" : String(account.headcount),
    revenue: account.revenue || "",
    funding: account.funding || "",
    hq: account.hq || "",
    region: account.region || "",
    offices: account.offices == null ? "" : String(account.offices),
    crmInUse: account.crmInUse || "",
    icpRationale: account.icpRationale || "",
    decisionMakers: account.decisionMakers || "",
    procurement: account.procurement || "",
    expansion: account.expansion || "Medium",
    growth: account.growth || "",
    techStack: (account.techStack || []).join(", "),
    challenges: (account.challenges || []).join(", "),
    ownerOperator: account.ownerOperator ? "Yes" : "No",
    singleThreaded: account.singleThreaded ? "Yes" : "No",
    msa: account.msa ? "Yes" : "No",
    owner: account.owner || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.accounts.update(account.id, {
        name: form.name.trim(),
        website: form.website.trim(),
        industry: form.industry.trim(),
        subIndustry: form.subIndustry.trim(),
        segment: form.segment,
        type: form.type,
        icp: form.icp,
        headcount: form.headcount.trim(),
        revenue: form.revenue.trim(),
        funding: form.funding.trim(),
        hq: form.hq.trim(),
        region: form.region.trim(),
        offices: form.offices.trim() === "" ? null : Number(form.offices),
        crmInUse: form.crmInUse.trim(),
        icpRationale: form.icpRationale.trim(),
        decisionMakers: form.decisionMakers.trim(),
        procurement: form.procurement.trim(),
        expansion: form.expansion,
        growth: form.growth.trim(),
        techStack: form.techStack.split(",").map(s => s.trim()).filter(Boolean),
        challenges: form.challenges.split(",").map(s => s.trim()).filter(Boolean),
        ownerOperator: form.ownerOperator === "Yes",
        singleThreaded: form.singleThreaded === "Yes",
        msa: form.msa === "Yes",
        owner: form.owner || null,
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save account.");
      setSaving(false);
    }
  }

  const users = assignableReps(null, form.owner);
  return (
    <Dialog title="Edit account" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
    </>}>
      <div className="form-field"><label>Account name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} autoFocus />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Segment</label>
          <select value={form.segment} onChange={e => set("segment", e.target.value)}>
            <option>SME</option><option>Mid-market</option><option>Enterprise</option>
          </select>
        </div>
        <div className="form-field"><label>Type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>Prospect</option><option>Customer</option><option>Partner</option><option>Investor</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Industry</label>
          <select value={form.industry} onChange={e => set("industry", e.target.value)}>
            <option value="">— None —</option>
            {D.INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            {/* Preserve any pre-existing free-form value not on the canonical list */}
            {form.industry && !D.INDUSTRIES.includes(form.industry) && (
              <option value={form.industry}>{form.industry} (legacy)</option>
            )}
          </select>
        </div>
        <div className="form-field"><label>Sub-industry</label>
          <input value={form.subIndustry} onChange={e => set("subIndustry", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Headcount</label>
          <select value={form.headcount} onChange={e => set("headcount", e.target.value)}>
            <option value="">— None —</option>
            {D.HEADCOUNT_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
            {form.headcount && !D.HEADCOUNT_BANDS.includes(form.headcount) && (
              <option value={form.headcount}>{form.headcount} (legacy)</option>
            )}
          </select>
        </div>
        <div className="form-field"><label>Revenue band</label>
          <select value={form.revenue} onChange={e => set("revenue", e.target.value)}>
            <option value="">— Unknown —</option>
            {D.REVENUE_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
            {form.revenue && !D.REVENUE_BANDS.includes(form.revenue) && (
              <option value={form.revenue}>{form.revenue} (legacy)</option>
            )}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Funding stage</label>
          <select value={form.funding} onChange={e => set("funding", e.target.value)}>
            <option value="">— Unknown —</option>
            {D.FUNDING_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            {form.funding && !D.FUNDING_STAGES.includes(form.funding) && (
              <option value={form.funding}>{form.funding} (legacy)</option>
            )}
          </select>
        </div>
        <div className="form-field"><label>Region</label>
          <select value={form.region} onChange={e => set("region", e.target.value)}>
            <option value="">— None —</option>
            {D.REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            {form.region && !D.REGIONS.includes(form.region) && (
              <option value={form.region}>{form.region} (legacy)</option>
            )}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>HQ</label>
          <input value={form.hq} onChange={e => set("hq", e.target.value)} placeholder="London, UK" />
        </div>
        <div className="form-field"><label>Offices</label>
          <input type="number" min="0" value={form.offices} onChange={e => set("offices", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Website</label>
          <input value={form.website} onChange={e => set("website", e.target.value)} placeholder="acme.com" />
        </div>
        <div className="form-field"><label>CRM in use</label>
          <input value={form.crmInUse} onChange={e => set("crmInUse", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>ICP tier</label>
          <select value={form.icp} onChange={e => set("icp", e.target.value)}>
            <option>Tier 1</option><option>Tier 2</option><option>Tier 3</option><option>Disqualified</option>
          </select>
        </div>
        <div className="form-field"><label>Expansion potential</label>
          <select value={form.expansion} onChange={e => set("expansion", e.target.value)}>
            <option>Low</option><option>Medium</option><option>High</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>ICP rationale</label>
        <textarea value={form.icpRationale} onChange={e => set("icpRationale", e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Decision makers</label>
          <input value={form.decisionMakers} onChange={e => set("decisionMakers", e.target.value)} placeholder="2-3" />
        </div>
        <div className="form-field"><label>Procurement</label>
          <input value={form.procurement} onChange={e => set("procurement", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Growth trajectory</label>
          <input value={form.growth} onChange={e => set("growth", e.target.value)} placeholder="High growth" />
        </div>
        <div className="form-field"><label>Owner</label>
          <select value={form.owner} onChange={e => set("owner", e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-field"><label>Tech stack</label>
        <input value={form.techStack} onChange={e => set("techStack", e.target.value)} placeholder="Comma-separated, e.g. SAP, Snowflake" />
      </div>
      <div className="form-field"><label>Key challenges</label>
        <input value={form.challenges} onChange={e => set("challenges", e.target.value)} placeholder="Comma-separated" />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Owner-operator</label>
          <select value={form.ownerOperator} onChange={e => set("ownerOperator", e.target.value)}>
            <option>No</option><option>Yes</option>
          </select>
        </div>
        <div className="form-field"><label>Single-threaded</label>
          <select value={form.singleThreaded} onChange={e => set("singleThreaded", e.target.value)}>
            <option>No</option><option>Yes</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>MSA in place</label>
        <select value={form.msa} onChange={e => set("msa", e.target.value)}>
          <option>No</option><option>Yes</option>
        </select>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function AccountsList({ navigate }) {
  const [filterSegment, setSegment] = useState("all");
  const [filterIcp, setIcp] = useState("all");
  const [filterType, setType] = useState("all");
  const [filterOwner, setOwner] = useState("all");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);

  const rows = D.ACCOUNTS.filter(a => {
    if (filterSegment !== "all" && a.segment !== filterSegment) return false;
    if (filterIcp !== "all" && a.icp !== filterIcp) return false;
    if (filterType !== "all" && a.type !== filterType) return false;
    if (filterOwner !== "all" && a.owner !== filterOwner) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <PageHead title="Accounts" sub={`${rows.length} accounts · primary lens is segment (SME / Mid-market / Enterprise)`}
        actions={<button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New account</button>} />
      <div className="card">
        <FilterToolbar count={rows.length} total={D.ACCOUNTS.length}>
          <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
          <select value={filterSegment} onChange={e => setSegment(e.target.value)}>
            <option value="all">All segments</option><option>SME</option><option>Mid-market</option><option>Enterprise</option>
          </select>
          <select value={filterIcp} onChange={e => setIcp(e.target.value)}>
            <option value="all">All ICP</option><option>Tier 1</option><option>Tier 2</option><option>Tier 3</option><option>Disqualified</option>
          </select>
          <select value={filterType} onChange={e => setType(e.target.value)}>
            <option value="all">All types</option><option>Prospect</option><option>Customer</option><option>Partner</option><option>Churned</option>
          </select>
          <select value={filterOwner} onChange={e => setOwner(e.target.value)}>
            <option value="all">All owners</option>
            {assignableReps("AE").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </FilterToolbar>
        <table className="tbl tbl-flush">
          <thead><tr>
            <th>Company</th><th>Segment</th><th>ICP</th><th>Headcount</th><th>Owner</th>
            <th className="right">Open opps</th><th>Health</th><th>Last activity</th><th>Expansion</th><th>Research</th>
          </tr></thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.id} onClick={() => navigate("sales/accounts/" + a.id)}>
                <td><span className="tbl-link">{a.name}</span> <span className="muted" style={{ fontSize: 11.5 }}>· {a.industry}</span></td>
                <td><Badge tone="slate">{a.segment}</Badge></td>
                <td><Badge tone={a.icp === "Tier 1" ? "amber" : a.icp === "Tier 2" ? "blue" : a.icp === "Disqualified" ? "red" : "slate"}>{a.icp}</Badge></td>
                <td><span className="muted">{a.headcount}</span></td>
                <td><Avatar user={a.owner} size={22} /></td>
                <td className="right numeric">{a.openOpps}</td>
                <td><HealthDot score={computeAccountHealth(a).score} /></td>
                <td>{fmtDateShort(a.lastActivity)}</td>
                <td><Badge tone={a.expansion === "High" ? "green" : a.expansion === "Medium" ? "amber" : "slate"}>{a.expansion}</Badge></td>
                <td><ResearchBadge surveys={a.researchSurveys} interviews={a.researchInterviews} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <NewAccountDialog onClose={() => setShowNew(false)} navigate={navigate} />}
    </>
  );
}

function AccountDetail({ id, navigate }) {
  const a = getAccount(id);
  const [tab, setTab] = useState("overview");
  const [action, setAction] = useState(null);
  if (!a) return <PageHead title="Not found" sub={`No account ${id}`} />;

  const contacts = D.CONTACTS.filter(c => c.accountId === a.id);
  const opps = D.OPPORTUNITIES.filter(o => o.accountId === a.id);
  const surveys = D.SURVEYS.filter(s => s.accountId === a.id);
  const interviews = D.INTERVIEWS.filter(i => i.accountId === a.id);
  const activities = (D.ACTIVITIES || []).filter(act => act.accountId === a.id);

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "contacts", label: "Contacts", count: contacts.length },
    { key: "opps", label: "Opportunities", count: opps.length },
    { key: "activity", label: "Activity" },
    { key: "research", label: "Research", count: surveys.length + interviews.length },
    { key: "insights", label: "Insights" },
    { key: "health", label: "Health" },
    // Partner tab only appears when this account is type='Partner'. Reuses
    // the cards defined in sales-partners.jsx (PartnerInfoCard, PartnerDealsCard).
    ...(a.type === "Partner" ? [{ key: "partner", label: "Partner" }] : []),
    { key: "audit", label: "Audit" },
  ];

  // aggregate insights from research
  const allPain = [...surveys, ...interviews].flatMap(r => r.pain || []);
  const painCounts = Object.fromEntries(allPain.reduce((m, p) => m.set(p, (m.get(p) || 0) + 1), new Map()));
  const useCases = [...new Set([...surveys, ...interviews].map(r => r.useCase).filter(Boolean))];
  const competitors = [...new Set(opps.flatMap(o => o.competitors))];

  return (
    <>
      <PageHead
        title={a.name}
        sub={<><Badge tone="slate">{a.segment}</Badge> <Badge tone={a.icp === "Tier 1" ? "amber" : "slate"}>{a.icp}</Badge> <Badge tone="slate">{a.type}</Badge> · {a.industry} · {a.hq}</>}
        actions={<>
          <ResearchBadge surveys={a.researchSurveys} interviews={a.researchInterviews} />
          <button className="btn btn-sm" onClick={() => setAction("edit")}>Edit</button>
          <button className="btn btn-sm" onClick={() => setAction("activity")}>Log activity</button>
          <button className="btn btn-primary btn-sm" onClick={() => setAction("newOpp")}>+ New opportunity</button>
        </>}
      />

      <Tabs tabs={tabs} current={tab} onChange={setTab} />

      <div className="detail">
        <div>
          {tab === "overview" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Firmographics & ICP</div></div>
              <div className="card-body">
                <div className="grid grid-2" style={{ gap: 24 }}>
                  <div className="kv">
                    <div className="kv-label">Industry</div><div className="kv-value">{a.industry} · <span className="muted">{a.subIndustry}</span></div>
                    <div className="kv-label">Headcount</div><div className="kv-value">{a.headcount}</div>
                    <div className="kv-label">Revenue band</div><div className="kv-value">{a.revenue}</div>
                    <div className="kv-label">Funding stage</div><div className="kv-value">{a.funding}</div>
                    <div className="kv-label">HQ / region</div><div className="kv-value">{a.hq} · {a.region}</div>
                    <div className="kv-label">Offices</div><div className="kv-value">{a.offices}</div>
                    <div className="kv-label">Website</div><div className="kv-value">{a.website}</div>
                    <div className="kv-label">CRM in use</div><div className="kv-value">{a.crmInUse || "—"}</div>
                    <div className="kv-label">Tech stack</div><div className="kv-value">{(a.techStack || []).length ? a.techStack.map(t => <Badge key={t} tone="slate">{t}</Badge>) : <span className="muted">—</span>}</div>
                  </div>
                  <div className="kv">
                    <div className="kv-label">Segment</div><div className="kv-value"><Badge tone="slate">{a.segment}</Badge> <span className="muted">{a.segment === "SME" ? "1-200 hc" : a.segment === "Mid-market" ? "201-1000 hc" : "1000+ hc"}</span></div>
                    <div className="kv-label">ICP tier</div><div className="kv-value"><Badge tone="amber">{a.icp}</Badge></div>
                    <div className="kv-label">ICP rationale</div><div className="kv-value muted" style={{ fontSize: 12.5 }}>{a.icpRationale}</div>
                    <div className="kv-label">Account score</div><div className="kv-value"><Donut value={a.score} /> </div>
                    <div className="kv-label">Owner-operator</div><div className="kv-value">{a.ownerOperator ? <Badge tone="amber">Yes</Badge> : "No"}</div>
                    <div className="kv-label">Decision makers</div><div className="kv-value">{a.decisionMakers}</div>
                    <div className="kv-label">Single-threaded</div><div className="kv-value">{a.singleThreaded ? <Badge tone="red">Risk</Badge> : <Badge tone="green">No</Badge>}</div>
                    <div className="kv-label">Procurement</div><div className="kv-value">{a.procurement}</div>
                    <div className="kv-label">MSA in place</div><div className="kv-value">{a.msa ? <Badge tone="green">Yes</Badge> : <Badge tone="slate">No</Badge>}</div>
                    <div className="kv-label">Expansion potential</div><div className="kv-value"><Badge tone={a.expansion === "High" ? "green" : a.expansion === "Medium" ? "amber" : "slate"}>{a.expansion}</Badge></div>
                    <div className="kv-label">Growth trajectory</div><div className="kv-value">{a.growth}</div>
                    <div className="kv-label">Key challenges</div><div className="kv-value">{(a.challenges || []).length ? a.challenges.map(c => <Badge key={c} tone="slate">{c}</Badge>) : <span className="muted">—</span>}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "contacts" && (
            <div className="card">
              <div className="card-head flex-between">
                <div>
                  <div className="card-title">Contacts</div>
                  <div className="card-sub">{contacts.length} at this account</div>
                </div>
                <div className="flex-gap">
                  <button className="btn btn-sm" onClick={() => setAction("uploadContacts")}>⤒ Upload contacts</button>
                  <button className="btn btn-primary btn-sm" onClick={() => setAction("newContact")}>+ New contact</button>
                </div>
              </div>
              {contacts.length === 0 ? (
                <div className="card-body muted">No contacts yet — add one with “+ New contact”, or “Upload contacts” to import several from a CSV.</div>
              ) : (
              <table className="tbl tbl-flush">
                <thead><tr><th>Name</th><th>Title</th><th>Role · active deal</th><th>Confidence</th><th>Last contacted</th><th>Opt-out</th><th>Research</th></tr></thead>
                <tbody>
                  {contacts.map(c => {
                    // Account-level Role column shows the contact's role on
                    // their most-recent open opportunity (option B). Role
                    // and confidence are per-deal — em dash when not on
                    // any active deal.
                    const activeOpp = getActiveOppForContact(c.id);
                    const oc = activeOpp ? getOppContactRole(activeOpp.id, c.id) : null;
                    return (
                      <tr key={c.id} onClick={() => navigate("sales/contacts/" + c.id)}>
                        <td><span className="tbl-link">{c.first} {c.last}</span></td>
                        <td className="muted">{c.title}</td>
                        <td>
                          {oc && oc.role
                            ? <><Badge tone={oc.role === "Champion" ? "amber" : oc.role === "Blocker" ? "red" : "slate"}>{oc.role}</Badge>{activeOpp ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· {activeOpp.name}</span> : null}</>
                            : <span className="muted">—</span>}
                        </td>
                        <td>{oc && oc.roleConfidence ? "●".repeat(oc.roleConfidence) : <span className="muted">—</span>}</td>
                        <td>{fmtDateShort(c.lastContact)}</td>
                        <td>{c.optOut ? <Badge tone="red">Yes</Badge> : "No"}</td>
                        <td><ResearchBadge surveys={c.surveys} interviews={c.interviews} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
          )}

          {tab === "opps" && (
            <div className="card">
              <table className="tbl tbl-flush">
                <thead><tr><th>Name</th><th>Stage</th><th className="right">ACV</th><th>Close</th><th>MEDDPICCR</th><th>Motion</th></tr></thead>
                <tbody>
                  {opps.map(o => {
                    const score = meddScore(o);
                    return (
                      <tr key={o.id} onClick={() => navigate("sales/opportunities/" + o.id)}>
                        <td><span className="tbl-link">{o.name}</span></td>
                        <td><StageBadge stage={o.stage} /></td>
                        <td className="right numeric">{fmtMoney(o.acv, o.currency)}</td>
                        <td>{fmtDateShort(o.closeDate)}</td>
                        <td>{score.got}/{score.total} <MeddPip opp={o} /></td>
                        <td className="muted">{o.motion}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab === "activity" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Unified activity timeline</div><div className="card-sub">all calls, emails, meetings, notes at this account</div></div>
              <ActivityTimeline activities={activities} empty="No activity logged for this account yet." />
            </div>
          )}

          {tab === "research" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Research at this account</div><div className="card-sub">Surveys + interviews across all contacts</div></div>
              <table className="tbl tbl-flush">
                <thead><tr><th>Type</th><th>Respondent</th><th>Role</th><th>Status</th><th>Date</th><th>Key finding</th></tr></thead>
                <tbody>
                  {surveys.map(s => {
                    const c = getContact(s.contactId) || {};
                    // Role at time of research is per-deal — pull from the
                    // survey's linked opportunity if any.
                    const oc = s.opp ? getOppContactRole(s.opp, s.contactId) : null;
                    return (
                      <tr key={s.id} onClick={() => navigate("research/surveys/" + s.id)}>
                        <td><Badge tone="blue">Survey</Badge> {s.type}</td>
                        <td><span className="tbl-link">{c.first} {c.last}</span></td>
                        <td className="muted">{oc ? oc.role : "—"}</td>
                        <td>{s.status}</td>
                        <td>{fmtDateShort(s.completed || s.sent)}</td>
                        <td className="muted" style={{ maxWidth: 320 }}>{s.keyQuote ? `"${s.keyQuote}"` : "—"}</td>
                      </tr>
                    );
                  })}
                  {interviews.map(i => {
                    const c = getContact(i.contactId) || {};
                    const oc = i.opp ? getOppContactRole(i.opp, i.contactId) : null;
                    return (
                      <tr key={i.id} onClick={() => navigate("research/interviews/" + i.id)}>
                        <td><Badge tone="amber">Interview</Badge> {i.type}</td>
                        <td><span className="tbl-link">{c.first} {c.last}</span></td>
                        <td className="muted">{oc ? oc.role : "—"}</td>
                        <td>{i.status}</td>
                        <td>{fmtDateShort(i.date)}</td>
                        <td className="muted" style={{ maxWidth: 320 }}>{i.keyQuote ? `"${i.keyQuote}"` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab === "insights" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Insights</div><div className="card-sub">Synthesised across all research touchpoints at this account</div></div>
              <div className="card-body">
                <div className="section-title">Pain categories</div>
                {Object.entries(painCounts).map(([p, n]) => (
                  <BarRow key={p} label={p} value={n} max={Math.max(...Object.values(painCounts), 1)} />
                ))}
                <div className="section-title">Use cases confirmed</div>
                <div className="flex-gap" style={{ flexWrap: "wrap" }}>{useCases.map(u => <Badge key={u} tone="slate">{u}</Badge>)}</div>
                <div className="section-title">Buying triggers</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {[...surveys, ...interviews].filter(r => r.trigger === "Yes").length} confirmed, {[...surveys, ...interviews].filter(r => r.trigger === "Unclear").length} unclear, {[...surveys, ...interviews].filter(r => r.trigger === "No").length} not present.
                </div>
                <div className="section-title">Competitors mentioned</div>
                <div className="flex-gap" style={{ flexWrap: "wrap" }}>{competitors.length ? competitors.map(c => <Badge key={c} tone="red">{c}</Badge>) : <span className="muted">None</span>}</div>
              </div>
            </div>
          )}

          {tab === "health" && (() => {
            const h = computeAccountHealth(a);
            return (
              <div className="card">
                <div className="card-head">
                  <div className="card-title">Customer health</div>
                  <div className="card-sub">Derived from activity, opportunities, research, and contact engagement</div>
                </div>
                <div className="card-body">
                  {a.type !== "Customer" ? <div className="muted">Not yet a customer.</div> : (
                    <>
                      <div className="kv">
                        <div className="kv-label">Health score</div>
                        <div className="kv-value">
                          {h.score == null
                            ? <span className="muted">— {h.reason ? `(${h.reason})` : ""}</span>
                            : <HealthDot score={h.score} />}
                        </div>
                        <div className="kv-label">Renewal date</div><div className="kv-value">{fmtDate(a.renewal)}</div>
                        <div className="kv-label">Lifetime TCV</div><div className="kv-value numeric">{fmtMoneyExact(a.tcvLifetime)}</div>
                        <div className="kv-label">Customer since</div><div className="kv-value">{fmtDate(a.customerSince)}</div>
                        <div className="kv-label">Executive sponsor</div><div className="kv-value">{getUser(a.sponsor)?.name || "—"}</div>
                        <div className="kv-label">CSM tier SLA</div><div className="kv-value">{a.sla || "—"}</div>
                      </div>
                      {h.components.length > 0 && (
                        <>
                          <div className="section-title">Score breakdown</div>
                          <table className="tbl tbl-flush">
                            <thead><tr><th>Component</th><th>Signal</th><th className="right">Points</th></tr></thead>
                            <tbody>
                              {h.components.map(c => (
                                <tr key={c.label} style={{ cursor: "default" }}>
                                  <td>{c.label}</td>
                                  <td className="muted">{c.detail}</td>
                                  <td className="right numeric">{c.points}/{c.max}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {tab === "partner" && a.type === "Partner" && (
            <>
              <PartnerInfoCard account={a} />
              <PartnerDealsCard account={a} navigate={navigate} />
            </>
          )}

          {tab === "audit" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Audit</div></div>
              <div className="card-body">
                <div className="kv">
                  <div className="kv-label">Created</div><div className="kv-value">{fmtDate(a.created)} by {getUser(a.owner)?.name}</div>
                  <div className="kv-label">Last activity</div><div className="kv-value">{fmtDate(a.lastActivity)}</div>
                  <div className="kv-label">Account ID</div><div className="kv-value-mono">{a.id}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <RightRail record={a} link={{ accountId: a.id }} />
      </div>
      {action === "edit" && <EditAccountDialog account={a} onClose={() => setAction(null)} />}
      {action === "activity" && <QuickActionDialog type="activity" link={{ accountId: a.id }} onClose={() => setAction(null)} onSave={() => setAction(null)} />}
      {action === "newOpp" && <NewOpportunityDialog accountId={a.id} onClose={() => setAction(null)} navigate={navigate} />}
      {action === "newContact" && <NewContactDialog defaultAccountId={a.id} onClose={() => setAction(null)} />}
      {action === "uploadContacts" && <BulkUploadContactsDialog accountId={a.id} accountName={a.name} onClose={() => setAction(null)} />}
    </>
  );
}

window.AccountsList = AccountsList;
window.AccountDetail = AccountDetail;
